import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

class Scroll {
  constructor($textReveal, $textRevealGroup, $fadeIn) {
    this.$textReveal = $textReveal;
    this.$textRevealGroup = $textRevealGroup;
    this.$fadeIn = $fadeIn;
    this.init();
  }

  init = () => {
    const units = this.collectUnits();
    const groups = this.groupUnits(units);
    this.setupGroupTriggers(groups);
  };

  collectUnits = () => {
    const units = [];

    this.$textReveal.each((i, el) => {
      this.wrapWords(el);
      const isLink = el.tagName === "A";
      units.push({
        el,
        type: "words",
        $words: $(el).find(".word"),
        $links: isLink ? $(el) : null,
      });
    });

    this.$textRevealGroup.each((i, group) => {
      $(group)
        .find("p, li, h3, h4, blockquote")
        .each((j, block) => {
          this.wrapWords(block);
          units.push({
            el: block,
            type: "words",
            $words: $(block).find(".word"),
            $links: $(block).find("a"),
            isListItem: block.tagName === "LI",
            richTextGroupEl: group,
          });
        });
      $(group).addClass("reveal-ready");
    });

    this.$fadeIn.each((i, el) => {
      units.push({ el, type: "fade" });
    });

    return units;
  };

  // Priorität für die Gruppenzuordnung:
  // 1. umgebende [data-scroll-section]
  // 2. umgebender Rich-Text-Container (falls keine Section)
  // 3. das Element selbst (Einzel-Fallback)
  groupUnits = (units) => {
    const groupMap = new Map();

    units.forEach((unit) => {
      const section = unit.el.closest("[data-scroll-section]");
      const key = section || unit.richTextGroupEl || unit.el;

      if (!groupMap.has(key)) {
        groupMap.set(key, { trigger: key, units: [] });
      }
      groupMap.get(key).units.push(unit);
    });

    const groups = Array.from(groupMap.values());

    // Reihenfolge INNERHALB der Gruppe = tatsächliche DOM-Reihenfolge
    groups.forEach((group) => {
      group.units.sort((a, b) => {
        const position = a.el.compareDocumentPosition(b.el);
        if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
      });
    });

    return groups;
  };

  setupGroupTriggers = (groups) => {
    const scrollY = window.scrollY;

    groups.forEach((group) => {
      const top = group.trigger.getBoundingClientRect().top + scrollY;
      const alreadyScrolledPast = top < scrollY - 50;

      group.units.forEach((unit) => {
        $(unit.el).addClass("reveal-ready");
        if (alreadyScrolledPast) {
          this.showInstant(unit);
        } else {
          this.setInitialState(unit);
        }
      });

      if (alreadyScrolledPast) return;

      ScrollTrigger.create({
        trigger: group.trigger,
        start: "top 85%",
        once: true,
        onEnter: () => this.playGroup(group.units),
      });
    });
  };

  setInitialState = (unit) => {
    if (unit.type === "fade") {
      gsap.set(unit.el, { y: 24, opacity: 0 });
      return;
    }
    gsap.set(unit.$words.toArray(), { yPercent: 120, opacity: 0 });
    if (unit.isListItem) {
      gsap.set(unit.el, { "--bullet-opacity": 0, "--bullet-scale": 0 });
    }
    if (unit.$links && unit.$links.length) {
      gsap.set(unit.$links.toArray(), { "--underline-reveal": 0 });
    }
  };

  showInstant = (unit) => {
    if (unit.type === "fade") {
      gsap.set(unit.el, { y: 0, opacity: 1 });
      return;
    }
    gsap.set(unit.$words.toArray(), { yPercent: 0, opacity: 1 });
    if (unit.isListItem) {
      gsap.set(unit.el, { "--bullet-opacity": 1, "--bullet-scale": 1 });
    }
    if (unit.$links && unit.$links.length) {
      gsap.set(unit.$links.toArray(), { "--underline-reveal": 1 });
    }
  };

  playGroup = (groupUnits) => {
    let index = 0;

    const playNext = () => {
      if (index >= groupUnits.length) return;
      const unit = groupUnits[index];
      index += 1;
      this.play(unit, playNext);
    };

    playNext();
  };

play = (unit, onDone) => {
  const tl = gsap.timeline();

  if (unit.type === "fade") {
    tl.to(unit.el, {
      y: 0,
      opacity: 1,
      duration: 0.4,
      ease: "power2.out",
      onComplete: onDone,
    });
    return;
  }

  tl.to(unit.$words.toArray(), {
    yPercent: 0,
    duration: 0.45,
    ease: "power2.out",
    stagger: 0.02,
    onComplete: onDone,
  }, 0);

  tl.to(unit.$words.toArray(), {
    opacity: 1,
    duration: 0.6,
    ease: "power1.out",
    stagger: 0.02,
  }, 0);

  if (unit.isListItem) {
    tl.to(
      unit.el,
      { "--bullet-opacity": 1, "--bullet-scale": 1, duration: 0.25, ease: "back.out(1.7)" },
      "<"
    );
  }

  if (unit.$links && unit.$links.length) {
    tl.to(
      unit.$links.toArray(),
      { "--underline-reveal": 1, duration: 0.25, ease: "power2.out" },
      "-=0.15"
    );
  }
};

  wrapWords = (el) => {
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          if (!child.textContent.trim()) return;
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((w) => {
            if (!w.trim()) {
              frag.appendChild(document.createTextNode(w));
            } else {
              const wrap = document.createElement("span");
              wrap.className = "word-wrap";
              const word = document.createElement("span");
              word.className = "word";
              word.textContent = w;
              wrap.appendChild(word);
              frag.appendChild(wrap);
            }
          });
          child.replaceWith(frag);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          walk(child);
        }
      });
    };
    walk(el);
  };
}

export default Scroll;